import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '@/auth/useAuth'
import type { UserEntity } from '@/types/api'
import { ROUTES } from './paths'

interface RequireEntityProps {
  /** Entity the caller must have. Pass an array to allow any of several. */
  entity: UserEntity | UserEntity[]
  children: ReactNode
  /** Where to send unauthorized users. Defaults to the in-app forbidden page. */
  redirectTo?: string
}

/**
 * Gate a route behind a coarse entity check (`ADMIN` / `STAFF`).
 *
 * Prefer `RequirePermission` for feature access — the backend authorizes by
 * permission, so an entity gate can drift from what the API actually allows. Use
 * this only where an entire area is genuinely admin-only regardless of grants.
 */
export function RequireEntity({ entity, children, redirectTo = ROUTES.forbidden }: RequireEntityProps) {
  const { user } = useAuth()
  const allowed = Array.isArray(entity)
    ? entity.includes(user?.entity as UserEntity)
    : user?.entity === entity
  if (!allowed) return <Navigate to={redirectTo} replace />
  return <>{children}</>
}

/**
 * Requires the account to be active (email verified, approved and activated) and
 * assigned to at least one group.
 *
 * Accounts still in onboarding are sent to the account-status page, which shows
 * the current step (awaiting approval or the activation code). Active accounts
 * with no group are sent to the awaiting-access notice: RBAC grants every
 * dashboard route through a group, so there is nothing for them to see yet.
 */
export function RequireApproved({ children }: { children: ReactNode }) {
  const { user, groups } = useAuth()
  if (!user) return null
  if (user.status !== 'APPROVED') return <Navigate to={ROUTES.activation} replace />
  if (groups.length === 0) return <Navigate to={ROUTES.awaitingAccess} replace />
  return <>{children}</>
}
